import { z } from "zod/v4"
import { UserIdSchema } from "../id"
import { CreatedAt, UpdatedAt } from "./system"


export const UserPushSettingDocumentBody = z.object({
  accept_push_of_history: z.boolean().default(true).describe(`スペースに関する通知のプッシュを受け取るか否かです。未設定時には true として扱われます。`),
  accept_push_of_following_user_actions: z.boolean().default(true).describe(`フォローしているユーザーのアクションに対応したプッシュを受け取るか否かです。未設定時には true として扱われます。`),
  accept_push_of_others: z.boolean().default(true).describe(`その他の通知を受信する場合はtrue。Push配信ツールで配信したお知らせなどが該当します。未設定時には true として扱われます。`),
  accept_call: z.boolean().default(true).describe(`全体コールを受け付けるか否かです。未設定時には true として扱われます。`),
  accept_super_call: z.boolean().default(true).describe(`スーパーコールを受け付けるか否かです。未設定時には true として扱われます。`),
  accept_voice_reply_push: z.boolean().default(true).describe(`ボイス返信のプッシュ通知を受け取るか否かです。未設定時には true として扱われます。`),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type UserPushSettingDocumentBody = z.infer<typeof UserPushSettingDocumentBody>

export enum PushKind {
  PushOfFollowingUserActions = 0,
  PushOfHistory = 1,
  Call = 2,
  CallOff = 3,
  LiveFollowerCall = 4,
  PushOfNotice = 5,
  PushOfSpaceMessage = 6,
}

const PushKindSchema = z.nativeEnum(PushKind).describe(
  `プッシュ通知の種別です。
  |
  |各定数の意味は以下の通りです。
  |
  | 0. \`PushOfFollowingUserActions\`
  |    - フォロー中ユーザーに関するプッシュ通知。
  | 1. \`PushOfHistory\`
  |    - りれき機能に関するプッシュ通知。
  | 2. \`Call\`
  |    - POPOPO のコール機能用のプッシュ通知のうち、単一ユーザー向けコール(1on1コール)・スペース内のフォロワー宛複数コール(まとめてコール)呼び出しを示す。
  |    - iOS 向けには APNs VoIP Push で配信される。
  | 3. \`CallOff\`
  |    - POPOPO のコール機能用のプッシュ通知のうち、コールの呼び出し中止を示す。
  |    - iOS 向けには配信されない。
  | 4. \`LiveFollowerCall\`
  |    - POPOPO のコール機能用のプッシュ通知のうち、ライブ中にできるフォロワーに対する複数コール(ユーザー向けには「スーパーコール」という機能)の呼び出しを示す。
  |    - iOS 向けには APNs VoIP Push で配信される。
  | 5. \`PushOfNotice\`
  |    - 管理ツールのPush配信で送信したものはここに該当する。
  | 6. \`PushOfSpaceMessage\`
  |    - スペース内でメッセージ(テキスト/画像/動画)が送信されたときに、同スペースのオフラインメンバーへ送られるプッシュ通知。
  `.replace(/^\s*\|?/mg, "")
)

export enum PushQoS { AT_MOST_ONCE = 0, AT_LEAST_ONCE = 1, }

const PushQoSSchema = z.nativeEnum(PushQoS).describe(
  `対応するプッシュ通知リクエストが保証すべき配信 QoS の種別です。
  |
  |各定数の意味は以下の通りです。
  |
  | 0. \`AT_MOST_ONCE\`
  |    - 重複配信を許さない
  |    - 障害時は配信自体が省略される
  | 2. \`AT_LEAST_ONCE\`
  |    - 重複配信を許す
  `.replace(/^\s*\|?/mg, "")
)

export const PushTarget =
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("user").describe(`指定したユーザーを対象とします。`),
      users: UserIdSchema.array().describe(`対象ユーザーのリストです。`),
    }),
    z.object({
      kind: z.literal("follower").describe(`特定のユーザーのフォロワー全員を対象とします。`),
      followee: UserIdSchema.describe(`フォローされているユーザーです。`),
    }),
  ]).describe(`プッシュ通知の対象を表します。`)

export type PushTarget = z.infer<typeof PushTarget>

export enum JobStatus {
  /** requested but not scheduled yet */
  REQUESTED = 0,

  /** scheduled */
  SCHEDULED = 1,

  /** taken by a worker, being processed */
  WIP = 2,

  /** done */
  DONE = 3,
}

const JobStatusSchema = z.nativeEnum(JobStatus).describe(
  `各種ワーカーにより処理されるジョブの状態を表します。
  |
  |各定数の意味は以下の通りです。
  |
  | 0. \`REQUESTED\`
  |    - ジョブに対応したドキュメントは作成されたがワーカーへ未通知
  | 1. \`SCHEDULED\`
  |    - ジョブに対応したドキュメントは作成されワーカーへ通知済み
  | 2. \`WIP\`
  |    - ワーカーがジョブを処理中
  | 3. \`DONE\`
  |    - ジョブの処理が完了している
  |
  `.replace(/^\s*\|?/mg, "")
)

const PushTTL = z.number().describe(
  `このプッシュ通知のデッドライン(UNIX timestamp ms)です。
  |
  |デッドラインを超えた通知は QoS の設定によらず破棄されます。
  `.replace(/^\s*\|?/mg, "")
)

const PushPriority = z.object({
  // https://firebase.google.com/docs/cloud-messaging/android/message-priority
  // 指定しない場合は FCM のデフォルト挙動。
  // (データメッセージは "normal", 通知メッセージは "high" が使用されそう)
  android: z.enum(["high", "normal"]).optional(),
  // https://developer.apple.com/documentation/usernotifications/sending-notification-requests-to-apns
  // 指定しない場合は APNs のデフォルト挙動 ("10" が使用されそう)
  apple: z.enum(["10", "5", "1"]).optional(),
})

export const PushContent = z.object({
  kind: PushKindSchema,
  data: z.object({
    fcm: z.any().optional(),
    apn: z.any().optional(),
  }).describe("プッシュ通知の通知内容です。"),
  qos: z.optional(PushQoSSchema),
  ttl: z.optional(PushTTL),
  priority: z.optional(PushPriority)
})

export type PushContent = z.infer<typeof PushContent>

export const PushPlanJobDocumentBody = z.object({
  target: PushTarget,
  status: JobStatusSchema,
  start_after: UserIdSchema.optional(),
  content: PushContent,
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type PushPlanJobDocumentBody = z.infer<typeof PushPlanJobDocumentBody>

export const PushSendJobDocumentBody = z.object({
  targets: UserIdSchema.array().describe(`プッシュ通知の送信先となるユーザーのリストです。`),
  status: JobStatusSchema,
  content: PushContent,
  plan: z.string(),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type PushSendJobDocumentBody = z.infer<typeof PushSendJobDocumentBody>
